import {DataTestAttrs, DataTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {createStyles, UnstyledButton, Space} from '@mantine/core';
import {StyleDeclaration} from 'aphrodite';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {HTMLAttributes, ReactNode, useCallback} from 'react';

import {assertAndContinue} from 'Asserts';
import {FONT_WEIGHT_PROXIMA_REGULAR, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import DataTestAttrsType from 'test/lib/cypress/DataTestAttrsType';
import getTestProps from 'test/lib/cypress/getTestProps';
import ButtonGroupContext from 'ui-library/buttons/ButtonGroupContext';
import {BLUE, BLUE_LIGHT, GRAY_DARK} from 'ui-library/Colors';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {omit} from 'util/omitKey';
import pick from 'util/pick';
import {ElementMouseEvent} from 'util/Types';

/*
 * These enums map to styles and sizes in `less/components/buttons.less`.
 */

export enum ButtonSize {
  MINI = 'mini',
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

export enum ButtonStyle {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  SECONDARY_WHITE = 'secondaryWhite',
  SECONDARY_BLACK = 'secondaryBlack',
  TERTIARY_BLUE = 'tertiaryBlue',
  TERTIARY_BLUE_POWDER = 'tertiaryBluePowder',
  TERTIARY_GRAY = 'tertiaryGray',
  TERTIARY_WHITE = 'tertiaryWhite',
  TERTIARY_WHITE_TRANSPARENT = 'tertiaryWhiteTransparent',
  SUCCESS = 'success',
  NEGATIVE = 'negative',
  WARNING = 'warning',
  WARNING_LIGHT = 'warningLight',
  WARNING_LIGHTEST = 'warningLightest',
  CIRCLE_ICON = 'circleIcon',
  UNBUTTON = 'unButton',
  /** A text-only button best used for anchor tag replacements */
  LINK = 'link',
  /** A text-only button that still has a block-like structure, essentially a button without a background */
  TEXT = 'text',
  /** A background-less, borderless button */
  TRANSPARENT = 'transparent',
}

interface ButtonElementProps extends DataTestProps {
  className: string;
  style: StyleDeclaration;
  'data-toggle': 'dropdown' | null;
  id?: string;
}

function validateButtonStyleAndSize(buttonStyle: ButtonStyle, buttonSize?: ButtonSize | null): void {
  switch (buttonStyle) {
    case ButtonStyle.UNBUTTON:
    case ButtonStyle.LINK:
      assertAndContinue(buttonSize == null, `Expected button size to be null for ${buttonStyle} button.`);
      break;
    default:
      assertAndContinue(buttonSize != null, `Expected button size to not be null for ${buttonStyle} button.`);
  }
}

function _elementProps(
  componentProps: ButtonProps | ButtonLinkProps,
  classes: ReturnType<typeof useButtonStyles>['classes'],
  cx: ReturnType<typeof useButtonStyles>['cx']
): ButtonElementProps {
  return {
    className: cx(
      'button',
      // Add the button style
      `button--${componentProps.buttonStyle}`,
      {
        [classes.textButton]: componentProps.buttonStyle === ButtonStyle.TEXT,
        [classes.transparentButton]: componentProps.buttonStyle === ButtonStyle.TRANSPARENT,
        [`button--${componentProps.size}${componentProps.growToFitContents ? '-min-height' : ''}`]: Boolean(
          componentProps.size
        ),
        // ... and extra classes to style icons
        'button--iconText': Boolean(componentProps.icon && componentProps.text),
        'button--icon': Boolean(
          componentProps.icon &&
            !componentProps.text &&
            componentProps.buttonStyle !== ButtonStyle.CIRCLE_ICON
        ),
        // TODO(T7108): We should keep Dropdown state in a component rather than
        // relying on Bootstrap.
        // ... and Bootstrap dropdown classes if it's a dropdown
        'dropdown-toggle': componentProps.isDropdown,
        // By default, a dropdown steals focus from document.activeElement when clicked. In cases when we
        // do not want to steal focus (e.g. if the activeElement has text selection and the dropdown
        // is used to style the selected text), preventDropdownFocus can be set to true.
        'dropdown-toggle--noFocus': componentProps.preventDropdownFocus,
        'button--autoHeight': componentProps.autoHeight,
        'button--fullWidth': componentProps.fullWidth,
        // Fix styling on link-buttons
        // eslint-disable-next-line benchlint/no-in-operator
        'u-display-inline-block': 'href' in componentProps ? Boolean(componentProps.href) : undefined,
        'is-disabled': componentProps.disabled,
        [classes.flexWidthButton]: componentProps.flexWidth,
      },
      // ...and optional extra classes.
      componentProps.className
    ),
    'data-toggle': componentProps.isDropdown && !componentProps.disabled ? 'dropdown' : null,
    id: componentProps.id,
    style: {color: componentProps.textColor, fontWeight: componentProps.fontWeight},
    ...getTestProps(componentProps.dataTestAttrs),
    // Include any additional <button> attributes (i.e. disabled, onClick).
  };
}

const useButtonWrapperStyles = createStyles(() => ({
  justifySpaceBetween: {
    justifyContent: 'space-between',
  },
}));

interface ButtonWrapperProps {
  buttonStyle: string;
  buttonTextRef?: React.RefObject<HTMLSpanElement>;
  hideDropdownCaret?: boolean;
  icon?: BenchlingIconName | JSX.Element;
  iconAltText?: string;
  isDropdown?: boolean;
  text?: ReactNode;
  textClassName?: string;
}

function ButtonWrapper(props: ButtonWrapperProps): JSX.Element {
  const {classes, cx} = useButtonWrapperStyles();
  return (
    <div
      className={cx('button-wrapper', {
        'button-circleIconWrapper': props.buttonStyle === ButtonStyle.CIRCLE_ICON,
        [classes.justifySpaceBetween]:
          !props.hideDropdownCaret && props.isDropdown && props.buttonStyle !== ButtonStyle.CIRCLE_ICON,
      })}
    >
      {props.icon != null &&
        (typeof props.icon === 'string' ? (
          <BenchlingIcon icon={props.icon} altText={props.iconAltText} />
        ) : (
          props.icon
        ))}
      <span className={classNames(props.textClassName)} ref={props.buttonTextRef}>
        {props.text}
      </span>
      {(props.icon != null || props.text != null) && props.isDropdown && <Space w="xs" />}
      {!props.hideDropdownCaret && props.isDropdown && props.buttonStyle !== ButtonStyle.CIRCLE_ICON && (
        <BenchlingIcon icon="chevron-down" altText="Show dropdown menu" />
      )}
    </div>
  );
}

const useButtonStyles = createStyles(() => ({
  root: {
    '&[data-disabled]': {
      // We commonly wrap disabled buttons in tooltips explaining why they are disabled. As such, we never
      // set `disabled` on the button itself, so that pointer events can bubble up. For a Mantine button,
      // we follow the pattern suggested by the vendor of passing data-disabled so that Mantine library
      // code is aware that the button is disabled, even if it should be bubbling up pointer events.
      // https://v6.mantine.dev/core/button/#disabled-button-with-tooltip
      pointerEvents: 'all',
    },
  },
  flexWidthButton: {
    flex: '1 1 0',
  },
  textButton: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: BLUE_LIGHT,
    backgroundColor: 'transparent',
    '&:hover, &:active, &:focus': {
      backgroundColor: 'transparent',
      boxShadow: 'none !important',
      color: BLUE,
    },
    '&[data-disabled]': {
      color: GRAY_DARK,
      '&:hover, &:active, &:focus': {
        color: GRAY_DARK,
      },
    },
  },
  transparentButton: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    backgroundColor: 'transparent',
    color: Color.BLACK,
    '&:hover, &:active, &:focus': {
      backgroundColor: 'transparent',
      boxShadow: 'none !important',
      color: Color.BLACK,
    },
  },
}));

ButtonWrapper.propTypes = {
  buttonStyle: PropTypes.string.isRequired,
  buttonTextRef: PropTypes.object,
  hideDropdownCaret: PropTypes.bool.isRequired,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  iconAltText: PropTypes.string,
  isDropdown: PropTypes.bool.isRequired,
  text: PropTypes.node,
  textClassName: PropTypes.string,
};

/*
 * Load /_design/buttons to view all the button types, styles, sizes, and other options.
 */

export interface ButtonProps {
  'aria-expanded'?: HTMLAttributes<HTMLButtonElement>['aria-expanded'];
  autoHeight?: boolean; // sets height: auto on the button element
  /**
   * Button style (i.e. primary, secondary, tertiary, etc.). See ButtonStyle for all options.
   */
  buttonStyle: ButtonStyle;
  buttonTextRef?: React.RefObject<HTMLSpanElement>;
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  fontWeight?: number;
  fullWidth?: boolean;
  flexWidth?: boolean;
  hideDropdownCaret?: boolean;
  icon?: BenchlingIconName | JSX.Element;
  iconAltText?: string;
  id?: string;
  isDropdown?: boolean;
  preventDropdownFocus?: boolean;
  onClick?: (e: ElementMouseEvent) => void;
  onMouseOut?: (e: ElementMouseEvent) => void;
  onMouseOver?: (e: ElementMouseEvent) => void;
  onMouseDown?: (e: ElementMouseEvent) => void;
  text?: ReactNode;
  textColor?: string;
  /**
   * Button size (i.e. mini, small, medium, large). See ButtonSize for all options.
   */
  size?: ButtonSize;
  /** Sets height: auto and min-height to the button size's standard height. This allows buttons to fit
   * their contents & also still have the standardized min size. */
  growToFitContents?: boolean;
  textClassName?: string;
  type?: 'submit' | 'button'; // The HTML spec allows type=reset, but we specifically do not!
  dataTestAttrs?: DataTestAttrs;
}

/**
 * @deprecated - Use Mantine's Button directly unless you are using a button variant that does not exist on
 * our implementation thereof. Currently supported: primary (blue with white text), secondary (white with blue
 * text), tertiary (gray with black text), warn (red with white text), ghost (white with blue text and no
 * border)
 */
export default function Button({onMouseDown, preventDropdownFocus, ...otherProps}: ButtonProps): JSX.Element {
  const {classes, cx} = useButtonStyles();
  const _handleMouseDown = useCallback(
    (e: ElementMouseEvent): void => {
      if (onMouseDown) {
        onMouseDown(e);
      }
      if (preventDropdownFocus) {
        e.preventDefault();
      }
    },
    [onMouseDown, preventDropdownFocus]
  );

  validateButtonStyleAndSize(otherProps.buttonStyle, otherProps.size);
  // More attribute props can be added as needed. Exclude disabled and handle styling with className, since
  // it prohibits tooltips from showing up.
  const buttonHTMLProps = pick(otherProps, 'aria-expanded', 'onMouseOut', 'onMouseOver', 'type');
  const buttonWrapperProps = pick(
    otherProps,
    'buttonStyle',
    'buttonTextRef',
    'hideDropdownCaret',
    'icon',
    'iconAltText',
    'isDropdown',
    'text',
    'textClassName'
  );
  return (
    <ButtonGroupContext.Consumer>
      {({inFullWidthButtonGroup}) => {
        const elementProps = _elementProps({flexWidth: inFullWidthButtonGroup, ...otherProps}, classes, cx);
        return (
          <UnstyledButton
            {...omit(elementProps, 'className')}
            {...buttonHTMLProps}
            className={cx(classes.root, otherProps.className, elementProps.className)}
            fw={
              otherProps.buttonStyle === ButtonStyle.LINK ||
              otherProps.buttonStyle === ButtonStyle.TEXT ||
              otherProps.buttonStyle === ButtonStyle.TRANSPARENT
                ? `${FONT_WEIGHT_PROXIMA_SEMIBOLD} !important`
                : `${FONT_WEIGHT_PROXIMA_REGULAR} !important`
            }
            // We commonly wrap disabled buttons in tooltips explaining why they are disabled. As such, we never
            // set `disabled` on the button itself, so that pointer events can bubble up. For a Mantine button,
            // we follow the pattern suggested by the vendor of passing data-disabled so that Mantine library
            // code is aware that the button is disabled, even if it should be bubbling up pointer events.
            // https://v6.mantine.dev/core/button/#disabled-button-with-tooltip
            {...(otherProps.disabled ? {'data-disabled': true} : {})}
            onMouseDown={_handleMouseDown}
            // Omit onClick if button is disabled so it cannot be triggered; this can alternatively be
            // accomplished with `pointer-events: none;` in CSS, but this additionally disallows other mouse
            // events (e.g. hover).
            onClick={otherProps.disabled ? undefined : otherProps.onClick}
            ta={
              otherProps.buttonStyle === ButtonStyle.LINK || otherProps.buttonStyle === ButtonStyle.UNBUTTON
                ? 'left'
                : 'center'
            }
          >
            <ButtonWrapper {...buttonWrapperProps} />
            {otherProps.children}
          </UnstyledButton>
        );
      }}
    </ButtonGroupContext.Consumer>
  );
}
Button.propTypes = {
  // buttonStyle specifies button style (i.e. primary, see ButtonStyle), named to avoid collisions
  // with HTML attributes.
  buttonStyle: (props: ButtonProps, ...args: [string, string, string, string]) => {
    const styleError = PropTypes.oneOf(Object.values(ButtonStyle))(props, ...args);
    if (styleError) {
      return styleError;
    }
    const {buttonStyle, text} = props;
    if (buttonStyle === ButtonStyle.CIRCLE_ICON && text != null) {
      return new Error(`Buttons with ${buttonStyle} style should not have text (icon-only styles).`);
    }
    return null;
  },
  buttonTextRef: PropTypes.object,
  // Add optional nodes, such as Pulses.
  children: PropTypes.node,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  dataTestAttrs: DataTestAttrsType,
  fullWidth: PropTypes.bool.isRequired,
  hideDropdownCaret: PropTypes.bool.isRequired,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  iconAltText: PropTypes.string,
  // isDropdown specifies whether the component is a dropdown, and renders dropdown caret if true.
  isDropdown: PropTypes.bool.isRequired,
  onClick: PropTypes.func,
  onMouseOut: PropTypes.func,
  onMouseOver: PropTypes.func,
  onMouseDown: PropTypes.func,
  // size specifies button size (mini, small, medium, large).
  size: PropTypes.oneOf(Object.values(ButtonSize)),
  // text is rendered as text in the button.
  text: (props: ButtonProps, ...args: [string, string, string, string]) => {
    const nodeError = PropTypes.node(props, ...args);
    if (nodeError) {
      return nodeError;
    }
    const {text, icon, isDropdown, children} = props;
    if (!text && !icon && !isDropdown && !children) {
      return new Error('Button should have one of text, icon, children, or isDropdown prop defined.');
    }
    return null;
  },
  textClassName: PropTypes.string,
  type: PropTypes.oneOf(['submit', 'button']).isRequired,
};
Button.defaultProps = {
  fullWidth: false,
  hideDropdownCaret: false,
  isDropdown: false,
  type: 'button',
};

/*
 * A Button-style link component that has the same styling as Button but renders a `<a>` element
 * with support for link attributes (i.e. `href`).
 */

interface ButtonLinkProps extends ButtonProps {
  href?: string;
  target?: string;
  rel?: string;
  type: never;
  dataTestAttrs?: DataTestAttrs;
}

export function ButtonLink(props: ButtonLinkProps): JSX.Element {
  const {classes, cx} = useButtonStyles();
  const linkHTMLProps = pick(props, 'href', 'target', 'rel');
  const buttonWrapperProps = pick(
    props,
    'buttonStyle',
    'buttonTextRef',
    'hideDropdownCaret',
    'icon',
    'iconAltText',
    'isDropdown',
    'text',
    'textClassName'
  );
  return (
    <a
      {..._elementProps(props, classes, cx)}
      {...linkHTMLProps}
      onClick={props.disabled ? undefined : props.onClick}
    >
      <ButtonWrapper {...buttonWrapperProps} />
      {props.children}
    </a>
  );
}

ButtonLink.propTypes = {
  ...Button.propTypes,
  href: PropTypes.string,
  target: PropTypes.string,
};
ButtonLink.defaultProps = Button.defaultProps;

Object.freeze(Button);
